import { useCallback, useMemo, useState } from 'react';
import type { CategorizeItem } from '../types';
import type { DecoratedCategorizeItem, CategorizeNoticeState } from './types';
import {
  APPROVED_STORAGE_KEY,
  DISMISSED_STORAGE_KEY,
  OVERRIDE_STORAGE_KEY,
  CATEGORY_OPTIONS,
} from './constants';
import {
  addCleanupHistoryEntry,
  removeCleanupHistoryEntry,
} from '../../../../../hooks/useCleanupHistory';

function loadIds(key: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(key);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveIds(key: string, ids: Set<string>): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(Array.from(ids)));
  } catch {}
}

function loadOverrides(): Record<string, string> {
  try {
    const raw = window.localStorage.getItem(OVERRIDE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function saveOverrides(map: Record<string, string>): void {
  try {
    window.localStorage.setItem(OVERRIDE_STORAGE_KEY, JSON.stringify(map));
  } catch {}
}

function categoryName(id: string): string {
  return CATEGORY_OPTIONS.find((c) => c.id === id)?.name ?? id;
}

interface UseCategorizeActionsResult {
  decoratedItems: DecoratedCategorizeItem[];
  notice: CategorizeNoticeState | null;
  approve: (id: string) => void;
  dismiss: (id: string) => void;
  changeCategory: (id: string, newCategoryId: string) => void;
  undo: () => void;
  approveAll: () => void;
  dismissAll: () => void;
}

export function useCategorizeActions(items: CategorizeItem[]): UseCategorizeActionsResult {
  const [approvedIds, setApprovedIds] = useState<Set<string>>(() => loadIds(APPROVED_STORAGE_KEY));
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => loadIds(DISMISSED_STORAGE_KEY));
  const [overrides, setOverrides] = useState<Record<string, string>>(() => loadOverrides());
  const [notice, setNotice] = useState<CategorizeNoticeState | null>(null);

  const decoratedItems = useMemo<DecoratedCategorizeItem[]>(() => {
    return items
      .filter((i) => !approvedIds.has(i.id) && !dismissedIds.has(i.id))
      .map((i) => {
        const override = overrides[i.id];
        const effectiveId = override ?? i.suggestedCategoryId;
        return {
          ...i,
          state: 'pending' as const,
          selectedCategoryId: effectiveId,
          selectedCategoryName: categoryName(effectiveId),
          isOverride: Boolean(override),
        };
      });
  }, [items, approvedIds, dismissedIds, overrides]);

  const approve = useCallback(
    (id: string) => {
      const item = items.find((i) => i.id === id);
      if (!item) return;
      const effectiveId = overrides[id] ?? item.suggestedCategoryId;
      const effectiveName = categoryName(effectiveId);
      const isOverride = Boolean(overrides[id]);
      const historyId = `h-${Date.now()}`;
      addCleanupHistoryEntry({
        id: historyId,
        dateChanged: new Date().toISOString(),
        job: 'categorize',
        jobLabel: 'Categorize',
        jobIcon: 'C',
        approvedBy: 'Claire Kemanzi',
        approvedByVela: false,
        description: `Categorized "${item.description}" as ${effectiveName}${isOverride ? ' (overrode Vela)' : ''}`,
        canUndo: true,
        undoPayload: { kind: 'categorize', transactionIds: [id] },
      });
      const next = new Set(approvedIds);
      next.add(id);
      setApprovedIds(next);
      saveIds(APPROVED_STORAGE_KEY, next);
      setNotice({
        message: `${item.description} categorized as ${effectiveName}.`,
        action: 'approve',
        itemIds: [id],
        historyIds: [historyId],
      });
    },
    [items, approvedIds, overrides]
  );

  const dismiss = useCallback(
    (id: string) => {
      const item = items.find((i) => i.id === id);
      if (!item) return;
      const next = new Set(dismissedIds);
      next.add(id);
      setDismissedIds(next);
      saveIds(DISMISSED_STORAGE_KEY, next);
      setNotice({
        message: `${item.description} dismissed.`,
        action: 'dismiss',
        itemIds: [id],
        historyIds: [],
      });
    },
    [items, dismissedIds]
  );

  const changeCategory = useCallback(
    (id: string, newCategoryId: string) => {
      const nextOverrides = { ...overrides, [id]: newCategoryId };
      setOverrides(nextOverrides);
      saveOverrides(nextOverrides);
    },
    [overrides]
  );

  const undo = useCallback(() => {
    if (!notice) return;
    if (notice.action === 'approve') {
      const next = new Set(approvedIds);
      notice.itemIds.forEach((id) => next.delete(id));
      setApprovedIds(next);
      saveIds(APPROVED_STORAGE_KEY, next);
      notice.historyIds.forEach((id) => removeCleanupHistoryEntry(id));
    } else {
      const next = new Set(dismissedIds);
      notice.itemIds.forEach((id) => next.delete(id));
      setDismissedIds(next);
      saveIds(DISMISSED_STORAGE_KEY, next);
    }
    setNotice(null);
  }, [notice, approvedIds, dismissedIds]);

  const approveAll = useCallback(() => {
    const pending = items.filter((i) => !approvedIds.has(i.id) && !dismissedIds.has(i.id));
    if (pending.length === 0) return;
    const next = new Set(approvedIds);
    const historyIds: string[] = [];
    pending.forEach((item, idx) => {
      next.add(item.id);
      const effectiveId = overrides[item.id] ?? item.suggestedCategoryId;
      const effectiveName = categoryName(effectiveId);
      const isOverride = Boolean(overrides[item.id]);
      const hid = `h-${Date.now()}-${idx}`;
      historyIds.push(hid);
      addCleanupHistoryEntry({
        id: hid,
        dateChanged: new Date().toISOString(),
        job: 'categorize',
        jobLabel: 'Categorize',
        jobIcon: 'C',
        approvedBy: 'Claire Kemanzi',
        approvedByVela: false,
        description: `Categorized "${item.description}" as ${effectiveName}${isOverride ? ' (overrode Vela)' : ''}`,
        canUndo: true,
        undoPayload: { kind: 'categorize', transactionIds: [item.id] },
      });
    });
    setApprovedIds(next);
    saveIds(APPROVED_STORAGE_KEY, next);
    setNotice({
      message: `${pending.length} transaction${pending.length !== 1 ? 's' : ''} categorized.`,
      action: 'approve',
      itemIds: pending.map((p) => p.id),
      historyIds,
    });
  }, [items, approvedIds, dismissedIds, overrides]);

  const dismissAll = useCallback(() => {
    const pending = items.filter((i) => !approvedIds.has(i.id) && !dismissedIds.has(i.id));
    if (pending.length === 0) return;
    const next = new Set(dismissedIds);
    pending.forEach((p) => next.add(p.id));
    setDismissedIds(next);
    saveIds(DISMISSED_STORAGE_KEY, next);
    setNotice({
      message: `${pending.length} transaction${pending.length !== 1 ? 's' : ''} dismissed.`,
      action: 'dismiss',
      itemIds: pending.map((p) => p.id),
      historyIds: [],
    });
  }, [items, approvedIds, dismissedIds]);

  return {
    decoratedItems,
    notice,
    approve,
    dismiss,
    changeCategory,
    undo,
    approveAll,
    dismissAll,
  };
}